import { useTranslation } from 'react-i18next';
import { firstStopOf, LADDER_LABEL_KEY, ladderTrailSubKey } from '@d-orca/shared/client/activities';
import type { SiteId, TaskId } from '@interfaces/IScheduler';
import { useSchedulerSite, useSchedulerTask } from '../../hooks/useSchedulerStore';
import { useTaskLadderOrPlanned } from '../../hooks/useTaskLadderOrPlanned';
import { hasChildren } from '../../lib/commitReadiness';
import type { LadderStep } from '../../lib/ladder';
import styles from './TaskLadderTrail.module.css';

// ---------------------------------------------------------------------------
// TaskLadderTrail — Planned ── Committed ── Done, the current stop filled and
// Trusstor's word written under Committed when there is one (2026-09-18 commit
// UX design, decision 2). The status pill picker it replaced asked the user to
// know the vocabulary; the trail shows it.
//
// It sits in the drawer HEADER, under the task's identity and above the tabs:
// where the task stands is true of the task, not of one tab, so it reads the
// same whether you are on Overview, Comments, Edit or Stock. The ACTIONS that
// move it — commit, mark done, take back — stay in the Overview's commit
// block, beside the promise they act on.
//
// Renders nothing for a parent (its leaves each stand somewhere, it does not)
// or a milestone (a date is hit or not hit, never promised).
// ---------------------------------------------------------------------------

interface ITaskLadderTrailProps {
  siteId: SiteId;
  taskId: TaskId;
}

export function TaskLadderTrail({ siteId, taskId }: ITaskLadderTrailProps) {
  const { t } = useTranslation();
  const site = useSchedulerSite(siteId);
  const task = useSchedulerTask(siteId, taskId);
  const ladder = useTaskLadderOrPlanned(task, taskId);
  if (!task) return null;
  if (task.isMilestone) return null;
  if (hasChildren(site?.tasks ?? [], task.id)) return null;

  // Under Committed: the activity's status (shared with the mobile trail).
  const subKey = ladderTrailSubKey(ladder, task.kind);
  const first = firstStopOf(ladder);

  return (
    <ol className={styles.ladder} aria-label={t('scheduler.ladder.label')}>
      <Stop
        step={0}
        current={ladder.step}
        tone={first === 'blocked' ? 'blocked' : undefined}
        label={t(LADDER_LABEL_KEY[first])}
      />
      <Stop step={1} current={ladder.step} label={t(LADDER_LABEL_KEY.committed)} sub={subKey ? t(subKey) : ''} />
      <Stop step={2} current={ladder.step} tone="done" label={t(LADDER_LABEL_KEY.done)} />
    </ol>
  );
}

function Stop({ step, current, label, sub, tone }: { step: LadderStep; current: LadderStep; label: string; sub?: string; tone?: 'blocked' | 'done' }) {
  const state = step < current ? styles.past : step === current ? styles.now : '';
  const toneClass = step === current && tone ? styles[tone] : '';
  return (
    <li className={`${styles.stop} ${state} ${toneClass}`} aria-current={step === current ? 'step' : undefined}>
      <i className={styles.pip} aria-hidden />
      <span className={styles.stopLabel}>{label}</span>
      <small className={styles.stopSub}>{sub}</small>
    </li>
  );
}
